import React, { useCallback, useEffect, useState } from 'react'
import { path, pathOr, isEmpty, empty } from 'ramda'
import { Formik, FastField } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash-es'
import {
  Modal,
  CurrencyField,
  TextField,
  FormField,
  Checkbox,
  CurrencyText,
  FormLoader,
  InfoBox,
  TextInput,
  Label,
} from '../../../../views/components'
import cx from 'classnames'
import { currencyUtils } from '../../../../utils'
import { PAYMENT_REFERENCE_MAX, PAYMENT_REFERENCE_PATTERN } from 'utils/validation'
import styles from './WithdrawModal.module.scss'
import { walletApiEvents } from '../../../api/wallet'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { apiStateSelectors } from 'modules/api/apiState'
import { partyApiSelectors } from 'modules/api/party'
import { bankingApiSelectors } from 'modules/api/banking'
import { TBankingPartyErrors } from 'modules/api/banking/types'
import { TPartyRequest, TPayoutRequest } from 'modules/api/wallet/types'

type TWithdrawPayload = TPartyRequest<TPayoutRequest>

type TProps = {
  isOpen: boolean
  onClose: () => void
  partyAccountId: string
  partyAccountBalance: number
  partyAccountAssetName: string
  partyId: string
  partyTag: string
  walletRef: string
  isConsolidatedBalance: boolean
}

const validationSchema = yup.object().shape({
  amount: yup
    .number()
    .required('required')
    .test('amount', function (amount: $TSFixMe) {
      if (amount <= 0) {
        return this.createError({
          path: this.path,
          message: 'must be greater than 0',
        })
      }
      if (amount > this.parent.partyAccountBalance) {
        return this.createError({
          path: this.path,
          message: "can't be greater than available funds",
        })
      }
      return true
    }),
  reference: yup
    .string()
    .required('required')
    .max(PAYMENT_REFERENCE_MAX, `must be ${PAYMENT_REFERENCE_MAX} characters or less`)
    .matches(
      PAYMENT_REFERENCE_PATTERN,
      'can only contain letters, numbers, and limited special characters: . - * , ( ) % + $ ; = @ ? : ~ / _',
    ),
  isConfirmed: yup.bool().oneOf([true], 'Required!'),
})

const ButtonContainer = ({ children }: any) => <div className={styles['button-container']}>{children}</div>

const WithdrawModal = ({
  isOpen,
  onClose,
  partyAccountId,
  partyAccountBalance,
  partyAccountAssetName,
  partyId,
  partyTag,
  walletRef,
  isConsolidatedBalance,
}: TProps) => {
  const dispatch = useDispatch()
  const [wasSubmitting, setWasSubmitting] = useState(false)

  const isSubmitting = isConsolidatedBalance
    ? useSelector(state => apiStateSelectors.isLoading(state)([walletApiEvents.consolidatedWithdraw_request]))
    : useSelector(state => apiStateSelectors.isLoading(state)([walletApiEvents.withdraw_request]))

  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  /**
   * apiState buckets a failed request's errors by `JSON.stringify` of the payload it was
   * *dispatched* with, so the lookup has to use that same `{ body, params }`. This used to
   * look up `{ partyId }` — a key nothing is ever stored under — so `hasApiErrors` was always
   * false and a failed withdrawal closed the modal without showing its error. Before the
   * first submit there is no payload yet, and so nothing to show.
   */
  const [submittedPayload, setSubmittedPayload] = useState<TWithdrawPayload | null>(null)
  const withdrawEvent = !submittedPayload
    ? null
    : isConsolidatedBalance
    ? walletApiEvents.consolidatedWithdraw_request(submittedPayload)
    : walletApiEvents.withdraw_request(submittedPayload)
  const generalErrors = withdrawEvent ? getGeneralFormErrorsByEvent(withdrawEvent) : []
  const fieldErrors = withdrawEvent ? getFormFieldErrorsByEvent(withdrawEvent) : {}
  const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

  const getPartyBankingErrors = useSelector(bankingApiSelectors.getBankingPartyById)
  const partyBankingErrors: TBankingPartyErrors | null | undefined = partyId ? getPartyBankingErrors(partyId) : null
  const bankingErrors =
    !partyBankingErrors || !Array.isArray(partyBankingErrors.errors)
      ? []
      : partyBankingErrors.errors.map(error => error.message)

  const partyName = useSelector(state => partyApiSelectors.getPartyNameById(state)(partyId))
  const partyBankDetails = useSelector(state => partyApiSelectors.getPartyBankDetails(state)(partyId))

  const handleClose = useCallback(
    resetForm => {
      resetForm()
      onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (isSubmitting) {
      setWasSubmitting(true)
    }
  }, [isSubmitting])

  const debouncedClose = useCallback(
    debounce(
      () => {
        onClose()
        setWasSubmitting(false)
      },
      1000,
      { leading: false },
    ),
    [onClose, setWasSubmitting],
  )

  useEffect(() => {
    if (!isSubmitting && wasSubmitting && !hasApiErrors) {
      debouncedClose()
    }
  }, [isSubmitting, wasSubmitting, debouncedClose, hasApiErrors])

  const handleWithdrawRequest = useCallback(
    ({ body, params }: TWithdrawPayload) => {
      setSubmittedPayload({ body, params })
      isConsolidatedBalance
        ? dispatch(walletApiEvents.consolidatedWithdraw_request({ body, params }))
        : dispatch(walletApiEvents.withdraw_request({ body, params }))
    },
    [dispatch, isConsolidatedBalance],
  )

  return (
    <Formik
      enableReinitialize
      validateOnMount={false}
      validationSchema={validationSchema}
      onSubmit={values => {
        const payload = isConsolidatedBalance
          ? {
              body: {
                party: partyId,
                asset: partyAccountAssetName,
                amount: values.amount,
                reference: values.reference,
              },
              params: { partyId },
            }
          : {
              body: {
                party: partyId,
                asset: partyAccountAssetName,
                amount: values.amount,
                reference: values.reference,
                partyTag,
                walletRef,
              },
              params: { partyId },
            }
        handleWithdrawRequest(payload)
      }}
      initialValues={{
        amount: 0,
        reference: '',
        isConfirmed: false,
        partyAccountBalance,
      }}
    >
      {({
        values,
        errors,
        touched,
        isValid,
        submitCount,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        resetForm,
      }) => {
        const confirmationCopy = `
          <p>I confirm I'm withdrawing ${currencyUtils.formatCurrency(
            values.amount || 0,
          )} from <strong>${partyName}'s</strong> transactional wallet to the account above. 
        `

        const formLoaderState = isSubmitting
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) ||
            hasApiErrors ||
            (partyBankingErrors !== undefined && !empty(partyBankingErrors))
          ? 'error'
          : undefined

        return (
          <Modal isOpen={isOpen} onClose={() => handleClose(resetForm)}>
            <Modal.Title>Withdraw funds</Modal.Title>
            {bankingErrors.length > 0 && (
              <InfoBox type="error">
                <p>There are issues with the bank details. Please fix by editing the contact and try again.</p>
                <p>If you have already updated the contacts banking details, please try again in 5 minutes.</p>
                <ul>
                  {bankingErrors.map((err, i) => (
                    <li key={`${err} ${i}`}>{err as string}</li>
                  ))}
                </ul>
              </InfoBox>
            )}
            <Modal.Body>
              <FormLoader
                buttonProps={{
                  children: 'Withdraw funds',
                  disabled: !isValid || ['loading', 'error'].includes(formLoaderState as string),
                }}
                onSubmit={handleSubmit}
                state={formLoaderState}
                ButtonContainer={ButtonContainer}
                errorText={hasApiErrors ? pathOr('Oops, something went wrong.', [0], generalErrors) : 'Resolve errors'}
              >
                <FormField>
                  <FastField
                    name="amount"
                    render={({ field }: any) => (
                      <CurrencyField
                        {...field}
                        label="Withdrawal amount"
                        error={path(['amount'], values) && path(['amount'], errors)}
                        onChange={({ target }: any) => setFieldValue('amount', target.value)}
                        onBlur={handleBlur}
                      />
                    )}
                    meta={{ partyAccountBalance }}
                  />
                </FormField>
                <div className={styles['available-funds-info']}>
                  <Label circular className={styles['available-funds-label']}>
                    Available funds: <CurrencyText>{partyAccountBalance}</CurrencyText>
                  </Label>
                </div>
                <FormField>
                  <FastField
                    name="reference"
                    render={({ field }: any) => (
                      <TextField
                        {...field}
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="reference"
                            value={values.reference}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        error={path(['reference'], values) && path(['reference'], errors)}
                        label="Statement reference"
                        labelHint="This will appear on the recipient's bank statement"
                      />
                    )}
                  />
                </FormField>
                <FormField>
                  <p className={styles['bank-details-label']}>Payment to</p>
                  <p className={styles['bank-details']}>
                    <span>
                      <strong>Bank:</strong> {partyBankDetails?.bank}
                    </span>
                    <span>
                      <strong>Account type:</strong> {partyBankDetails?.accountType}
                    </span>
                    <span>
                      <strong>Account number:</strong> *****{partyBankDetails?.accountNumber?.substr(-4, 4)}
                    </span>
                    <span>
                      <strong>Account holder:</strong> {partyBankDetails?.accountName}
                    </span>
                  </p>
                </FormField>
                <FormField>
                  <Checkbox
                    className={cx(styles.confirmation, {
                      [styles['has-error']]: submitCount > 0 && path(['isConfirmed'], errors),
                    })}
                    name="isConfirmed"
                    label={<div dangerouslySetInnerHTML={{ __html: confirmationCopy }} />}
                    checked={values.isConfirmed}
                    onChange={(e: any) => setFieldValue('isConfirmed', e.checked)}
                    onBlur={handleBlur}
                    error={submitCount > 0 && path(['isConfirmed'], errors)}
                  />
                </FormField>
              </FormLoader>
            </Modal.Body>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default WithdrawModal
